// dev-portal/src/pages/auth/AdminLogin.jsx

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import Footer from '../../components/shared/Footer';
import { loadRecaptcha, executeRecaptcha, showRecaptchaBadge, hideRecaptchaBadge } from '../../utils/recaptcha';

export default function AdminLogin() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load reCAPTCHA on mount
  useEffect(() => {
    loadRecaptcha().catch(err => console.warn('Failed to load reCAPTCHA:', err));
    showRecaptchaBadge();
    return () => {
      hideRecaptchaBadge();
    };
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) {
      setError('Please fill in both email and password');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('ADMIN_LOGIN');

      const res = await api.post('/api/admin-login', {
        ...form,
        recaptcha_token: recaptchaToken
      });
      if (res.data && res.data.access_token && res.data.role) {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('role', res.data.role);
        // Full page load so every app-level context (company switcher,
        // permissions, etc.) starts fresh for this session.
        window.location.href = res.data.role === 'admin' ? '/admin' : '/dashboard';
      } else {
        setError('Invalid response from the server. Please try again.');
      }
    } catch (err) {
      console.error('Admin login error:', err);
      const errorMessage = err.response?.data?.detail || 'Admin login failed. Please try again.';
      if (errorMessage.includes('reCAPTCHA')) {
        setError('Security verification failed. Please try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 w-full max-w-md space-y-4">
          <div className="flex justify-between items-center mb-4">
            <Link to="/" className="text-sm text-blue-600 hover:underline">
              ← Back to Marketplace
            </Link>
            <Link to="/portal" className="text-sm text-blue-600 hover:underline">
              Developer Portal →
            </Link>
          </div>

          <h2 className="text-2xl font-bold text-center text-red-700">Admin Login</h2>

          {error && (
            <div className="bg-red-100 text-red-700 p-2 rounded text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              type="email"
              name="email"
              value={form.email}
              placeholder="Admin Email"
              onChange={handleChange}
            />
            <input
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              type="password"
              name="password"
              value={form.password}
              placeholder="Password"
              onChange={handleChange}
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In as Admin'}
            </button>
          </form>

          <div className="text-center text-sm text-gray-500 mt-4 pt-4 border-t">
            <Link to="/" className="text-gray-600 hover:text-gray-800">
              Browse Marketplace →
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}